import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BackButton } from '../components/ui/back-button';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  Dices, 
  CheckCircle, 
  XCircle, 
  Plus, 
  Edit, 
  Trash2, 
  Download, 
  ExternalLink, 
  MapPin, 
  Video,
  ChevronLeft,
  ChevronRight,
  List,
  Grid3X3,
  CalendarDays
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { eventsAPI, gamesAPI, tablesAPI } from '../services/api';
import SEO from '../components/SEO';
import { toast } from 'sonner';

// Title shown everywhere: "Titre - Table X" when a table is set
const fullTitle = (event) => (event.table_name ? `${event.title} - ${event.table_name}` : event.title);

// Helper function to generate ICS file content
const generateICS = (event) => {
  const startDate = new Date(`${event.date}T${event.time}`);
  const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
  
  const formatDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  
  const location = event.location || (event.online_link ? 'En ligne' : 'Just Rôle It');
  
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Just Rôle It//FR
BEGIN:VEVENT
UID:${event.event_id}@justroleit.fr
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${fullTitle(event)} - ${event.game_type}
DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}${event.online_link ? '\\n\\nLien: ' + event.online_link : ''}
LOCATION:${location}
END:VEVENT
END:VCALENDAR`;
  
  return icsContent;
};

// Helper to create Google Calendar URL
const getGoogleCalendarUrl = (event) => {
  const startDate = new Date(`${event.date}T${event.time}`);
  const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
  
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  
  const location = event.location || (event.online_link ? 'En ligne' : 'Just Rôle It');
  const description = (event.description || '') + (event.online_link ? '\n\nLien: ' + event.online_link : '');
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${fullTitle(event)} - ${event.game_type}`,
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    details: description,
    location: location
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Helper to download ICS file
const downloadICS = (event) => {
  const icsContent = generateICS(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success('Fichier calendrier téléchargé !');
};

// French day names
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Calendar helper functions
const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
  return new Date(d.setDate(diff));
};

const getEndOfWeek = (date) => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return end;
};

const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Convert to Monday start (0 = Monday)
};

const formatDateKey = (date) => {
  return date.toISOString().split('T')[0];
};

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameFilter, setGameFilter] = useState('all');
  const [editingEvent, setEditingEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('agenda'); // 'agenda', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    game_type: '',
    description: '',
    max_players: 6,
    location: '',
    online_link: '',
    session_type: 'oneshot',
    is_public: true,
    table_id: ''
  });
  const [tables, setTables] = useState([]);
  const { user, isAdmin, canCreateEvents, isMember } = useAuth();

  useEffect(() => {
    loadEvents();
    loadGames();
  }, []);

  // Tables list is only needed by event creators (members+)
  useEffect(() => {
    if (user && canCreateEvents()) {
      tablesAPI.getAll().then(setTables).catch((e) => console.error('Error loading tables:', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);


  const loadGames = async () => {
    try {
      const data = await gamesAPI.getAll();
      setGames(data);
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await eventsAPI.getAll();
      setEvents(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des événements');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter events by game tag
  const filteredEvents = gameFilter === 'all' 
    ? events 
    : events.filter(event => event.game_type === gameFilter);

  // Group events by date for calendar views
  const eventsByDate = useMemo(() => {
    const grouped = {};
    filteredEvents.forEach(event => {
      const dateKey = event.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    // Sort events by time within each day
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.time.localeCompare(b.time));
    });
    return grouped;
  }, [filteredEvents]);

  const handleJoinEvent = async (eventId) => {
    try {
      await eventsAPI.join(eventId);
      toast.success('Inscription réussie !');
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    }
  };

  const handleLeaveEvent = async (eventId) => {
    try {
      await eventsAPI.leave(eventId);
      toast.success('Désinscription réussie');
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désinscription');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await eventsAPI.create(formData);
      toast.success('Événement créé !');
      setIsDialogOpen(false);
      resetForm();
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      await eventsAPI.update(editingEvent, formData);
      toast.success('Événement modifié !');
      setIsDialogOpen(false);
      setEditingEvent(null);
      resetForm();
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    
    try {
      await eventsAPI.delete(eventId);
      toast.success('Événement supprimé');
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event.event_id);
    setFormData({
      title: event.title,
      date: event.date,
      time: event.time,
      game_type: event.game_type,
      description: event.description,
      max_players: event.max_players,
      location: event.location || '',
      online_link: event.online_link || '',
      session_type: event.session_type || 'oneshot',
      is_public: event.is_public !== false,
      table_id: event.table_id || ''
    });
    setSelectedEvent(null);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      game_type: '',
      description: '',
      max_players: 6,
      location: '',
      online_link: '',
      session_type: 'oneshot',
      is_public: true,
      table_id: ''
    });
    setEditingEvent(null);
  };

  // Helper to check if user can join an event
  const canJoinEvent = (event) => {
    if (!user) return false; // Visitors cannot join
    if (isMember()) return true; // Members can join any event
    // Guests can only join public events
    return event.is_public !== false;
  };

  // Get session type label
  const getSessionTypeLabel = (type) => {
    const labels = {
      'oneshot': 'One-Shot',
      'mini_campaign': 'Mini Campagne',
      'campaign': 'Campagne'
    };
    return labels[type] || type;
  };

  // Navigation functions
  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get week days for weekly view
  const getWeekDays = () => {
    const start = getStartOfWeek(currentDate);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Get month grid for monthly view
  const getMonthGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const grid = [];
    let week = [];
    
    // Previous month days
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
    
    for (let i = firstDay - 1; i >= 0; i--) {
      week.push({
        date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      week.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
      
      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }
    
    // Next month days
    if (week.length > 0) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      let nextDay = 1;
      while (week.length < 7) {
        week.push({
          date: new Date(nextYear, nextMonth, nextDay++),
          isCurrentMonth: false
        });
      }
      grid.push(week);
    }
    
    return grid;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Render Event Card (for Agenda view)
  const renderEventCard = (event) => {
    const userInEvent = event.user_is_participating;
    const eventFull = event.current_players >= event.max_players;
    const isPublicEvent = event.is_public !== false;
    const canJoin = canJoinEvent(event);

    return (
      <Card 
        key={event.event_id} 
        className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-6 md:p-8 hover:border-purple-400/50 transition-all hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.3)]"
        data-testid={`event-card-${event.event_id}`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
          <div className="flex-1 space-y-3 md:space-y-4">
            {/* Title and Badges */}
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-xl md:text-3xl font-bold text-purple-300" data-testid="event-card-title">{fullTitle(event)}</h2>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {/* Session Type Badge */}
                {event.session_type && (
                  <Badge className="bg-purple-600/60 text-purple-200 border-purple-400/50 text-xs">
                    {getSessionTypeLabel(event.session_type)}
                  </Badge>
                )}
                {/* Public/Private Badge */}
                <Badge className={isPublicEvent 
                  ? "bg-green-600/60 text-green-200 border-green-400/50 text-xs" 
                  : "bg-orange-600/60 text-orange-200 border-orange-400/50 text-xs"
                }>
                  {isPublicEvent ? 'Public' : 'Membres'}
                </Badge>
                {userInEvent && (
                  <Badge className="bg-green-600/80 text-white border-green-400/50">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Inscrit
                  </Badge>
                )}
                {eventFull && !userInEvent && (
                  <Badge className="bg-red-600/80 text-white border-red-400/50">
                    <XCircle className="h-3 w-3 mr-1" />
                    Complet
                  </Badge>
                )}
              </div>
            </div>

            {/* Game Type */}
            <div className="flex items-center space-x-2">
              <Dices className="h-5 w-5 text-purple-400" />
              <span className="text-purple-400 font-semibold">{event.game_type}</span>
            </div>

            {/* Description */}
            {event.description && (
              <p className="text-gray-300 leading-relaxed text-sm md:text-base">{event.description}</p>
            )}

            {/* Date and Time */}
            <div className="flex flex-wrap gap-3 md:gap-4 text-sm">
              <div className="flex items-center space-x-2 text-gray-400">
                <CalendarIcon className="h-4 w-4 text-purple-400" />
                <span>{new Date(event.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Clock className="h-4 w-4 text-purple-400" />
                <span>{event.time}</span>
              </div>
            </div>

            {/* Location and Online Link - Only visible to logged-in users */}
            {user && (event.location || event.online_link) && (
              <div className="flex flex-wrap gap-3 md:gap-4 text-sm">
                {event.location && (
                  <div className="flex items-center space-x-2 text-gray-400">
                    <MapPin className="h-4 w-4 text-green-400" />
                    <span>{event.location}</span>
                  </div>
                )}
                {event.online_link && (
                  <a 
                    href={event.online_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Video className="h-4 w-4" />
                    <span>Rejoindre en ligne</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}

            {/* Players */}
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-purple-400" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">
                    {event.current_players} / {event.max_players} joueurs
                  </span>
                  <span className="text-sm font-bold text-purple-400">
                    {Math.round((event.current_players / event.max_players) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-black/50 rounded-full h-2 border border-purple-500/30">
                  <div 
                    className="bg-gradient-to-r from-purple-600 to-purple-400 h-full rounded-full transition-all duration-500"
                    style={{ width: `${(event.current_players / event.max_players) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Participants - Only visible to logged-in users */}
            {user && event.participants.length > 0 && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Participants :</p>
                <div className="flex flex-wrap gap-2">
                  {event.participants.map((participant, index) => (
                    <Badge key={index} className="bg-purple-900/50 text-purple-300 border-purple-500/40">
                      {participant.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="md:min-w-[180px] space-y-2">
            {/* Join/Leave Button - Different states based on user role and event visibility */}
            {!user ? (
              /* Visitor (not logged in) - Show login prompt */
              <Button
                onClick={() => window.location.href = '/login'}
                variant="outline"
                className="w-full border-2 border-purple-500/50 text-purple-400 hover:bg-purple-900/30 py-4 md:py-6 font-bold"
                data-testid="login-to-join-btn"
              >
                Connectez-vous pour rejoindre
              </Button>
            ) : userInEvent ? (
              /* User is already in event - Show leave button */
              <Button
                onClick={() => handleLeaveEvent(event.event_id)}
                variant="outline"
                className="w-full border-2 border-red-500/50 text-red-400 hover:bg-red-900/30 hover:text-red-300 py-4 md:py-6 font-bold"
                data-testid="leave-event-btn"
              >
                Se désinscrire
              </Button>
            ) : !canJoin ? (
              /* Guest trying to join private event - Show restricted message */
              <Button
                disabled
                className="w-full bg-orange-600/30 text-orange-300 cursor-not-allowed py-4 md:py-6 font-bold"
                data-testid="members-only-btn"
              >
                Réservé aux membres
              </Button>
            ) : (
              /* Can join - Show join button */
              <Button
                onClick={() => handleJoinEvent(event.event_id)}
                disabled={eventFull}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 disabled:from-gray-600 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold py-4 md:py-6 neon-button"
                data-testid="join-event-btn"
              >
                {eventFull ? 'Complet' : 'Rejoindre'}
              </Button>
            )}
            
            {/* Calendar Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full border-2 border-purple-500/50 text-purple-400 hover:bg-purple-900/30"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Ajouter au calendrier
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-black/95 border-purple-500/50">
                <DropdownMenuItem 
                  onClick={() => window.open(getGoogleCalendarUrl(event), '_blank')}
                  className="text-purple-300 hover:bg-purple-900/50 cursor-pointer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Google Calendar
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => downloadICS(event)}
                  className="text-purple-300 hover:bg-purple-900/50 cursor-pointer"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Fichier ICS (Outlook, Apple)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {(isAdmin() || (canCreateEvents() && event.created_by === user?.user_id)) && (
              <>
                <Button
                  onClick={() => openEditDialog(event)}
                  variant="outline"
                  className="w-full border-2 border-purple-500/50 text-purple-400 hover:bg-purple-900/30"
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Modifier
                </Button>
                <Button
                  onClick={() => handleDeleteEvent(event.event_id)}
                  variant="outline"
                  className="w-full border-2 border-red-500/50 text-red-400 hover:bg-red-900/30"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    );
  };

  // Render Mini Event (for calendar cells)
  const renderMiniEvent = (event, compact = false) => {
    const userInEvent = event.user_is_participating;
    const eventFull = event.current_players >= event.max_players;
    
    return (
      <div
        key={event.event_id}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedEvent(event);
        }}
        className={`cursor-pointer rounded px-2 py-1 text-xs truncate transition-all hover:opacity-80 ${
          userInEvent 
            ? 'bg-green-600/80 text-white' 
            : eventFull 
              ? 'bg-red-600/60 text-white'
              : 'bg-purple-600/80 text-white'
        }`}
        title={`${event.time} - ${fullTitle(event)}`}
      >
        {!compact && <span className="font-semibold mr-1">{event.time}</span>}
        {fullTitle(event)}
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekDays = getWeekDays();
    const weekStart = getStartOfWeek(currentDate);
    const weekEnd = getEndOfWeek(currentDate);
    
    return (
      <div className="space-y-4">
        {/* Week Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <h2 className="text-base md:text-xl font-bold text-purple-300">
            {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {weekEnd.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
          </h2>
          <div className="flex gap-1 md:gap-2">
            <Button variant="outline" size="sm" onClick={navigatePrev} className="border-purple-500/50 text-purple-400 h-10 w-10 md:h-9 md:w-auto p-0 md:px-3">
              <ChevronLeft className="h-5 w-5 md:h-4 md:w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="border-purple-500/50 text-purple-400 h-10 md:h-9 px-3 text-sm">
              {"Aujourd'hui"}
            </Button>
            <Button variant="outline" size="sm" onClick={navigateNext} className="border-purple-500/50 text-purple-400 h-10 w-10 md:h-9 md:w-auto p-0 md:px-3">
              <ChevronRight className="h-5 w-5 md:h-4 md:w-4" />
            </Button>
          </div>
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7 gap-1 md:gap-2">
          {/* Day Headers */}
          {DAYS_FR.map((day, idx) => (
            <div key={day} className="text-center text-xs md:text-sm font-semibold text-purple-400 py-1 md:py-2">
              <span className="hidden md:inline">{DAYS_FULL_FR[idx]}</span>
              <span className="md:hidden">{day}</span>
            </div>
          ))}
          
          {/* Day Cells */}
          {weekDays.map((day) => {
            const dateKey = formatDateKey(day);
            const dayEvents = eventsByDate[dateKey] || [];
            const today = isToday(day);
            
            return (
              <Card 
                key={dateKey}
                className={`min-h-[100px] md:min-h-[150px] p-1 md:p-2 border transition-all ${
                  today 
                    ? 'border-purple-500 bg-purple-900/30' 
                    : 'border-purple-500/30 bg-black/30 hover:border-purple-500/50'
                }`}
              >
                <div className={`text-right text-xs md:text-sm font-bold mb-1 md:mb-2 ${today ? 'text-purple-300' : 'text-gray-400'}`}>
                  {day.getDate()}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 2).map(event => renderMiniEvent(event, true))}
                  {dayEvents.length > 2 && (
                    <div className="text-[10px] md:text-xs text-purple-400 text-center">
                      +{dayEvents.length - 2}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const monthGrid = getMonthGrid();
    
    return (
      <div className="space-y-4">
        {/* Month Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <h2 className="text-base md:text-xl font-bold text-purple-300">
            {MONTHS_FR[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <div className="flex gap-1 md:gap-2">
            <Button variant="outline" size="sm" onClick={navigatePrev} className="border-purple-500/50 text-purple-400 h-10 w-10 md:h-9 md:w-auto p-0 md:px-3">
              <ChevronLeft className="h-5 w-5 md:h-4 md:w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="border-purple-500/50 text-purple-400 h-10 md:h-9 px-3 text-sm">
              {"Aujourd'hui"}
            </Button>
            <Button variant="outline" size="sm" onClick={navigateNext} className="border-purple-500/50 text-purple-400 h-10 w-10 md:h-9 md:w-auto p-0 md:px-3">
              <ChevronRight className="h-5 w-5 md:h-4 md:w-4" />
            </Button>
          </div>
        </div>

        {/* Month Grid */}
        <div className="border border-purple-500/30 rounded-lg overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-purple-900/30">
            {DAYS_FR.map((day, idx) => (
              <div key={day} className="text-center text-xs md:text-sm font-semibold text-purple-400 py-1 md:py-2 border-b border-purple-500/30">
                <span className="hidden md:inline">{DAYS_FULL_FR[idx]}</span>
                <span className="md:hidden">{day}</span>
              </div>
            ))}
          </div>
          
          {/* Weeks */}
          {monthGrid.map((week, weekIdx) => (
            <div key={weekIdx} className="grid grid-cols-7">
              {week.map(({ date, isCurrentMonth }) => {
                const dateKey = formatDateKey(date);
                const dayEvents = eventsByDate[dateKey] || [];
                const today = isToday(date);
                
                return (
                  <div 
                    key={dateKey}
                    onClick={() => {
                      if (dayEvents.length > 0) {
                        setSelectedEvent(dayEvents[0]);
                      }
                    }}
                    className={`min-h-[60px] md:min-h-[100px] p-0.5 md:p-2 border-b border-r border-purple-500/20 transition-all cursor-pointer ${
                      !isCurrentMonth ? 'bg-black/50 opacity-50' : 'bg-black/30'
                    } ${today ? 'bg-purple-900/40' : ''} hover:bg-purple-900/20 ${dayEvents.length > 0 ? 'cursor-pointer' : ''}`}
                  >
                    <div className={`text-right text-[10px] md:text-sm font-bold mb-0.5 md:mb-1 ${
                      today ? 'text-purple-300' : isCurrentMonth ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-0.5 hidden md:block">
                      {dayEvents.slice(0, 2).map(event => renderMiniEvent(event, true))}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-purple-400 text-center">
                          +{dayEvents.length - 2}
                        </div>
                      )}
                    </div>
                    {/* Mobile: Show dot indicator */}
                    <div className="md:hidden flex justify-center">
                      {dayEvents.length > 0 && (
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Agenda View
  const renderAgendaView = () => {
    // Sort events by date
    const sortedEvents = [...filteredEvents].sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.time.localeCompare(b.time);
    });

    return (
      <div className="space-y-6">
        {sortedEvents.length === 0 ? (
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-12 text-center">
            <p className="text-gray-400 text-lg">
              {gameFilter !== 'all' ? `Aucun événement pour "${gameFilter}"` : 'Aucun événement pour le moment'}
            </p>
          </Card>
        ) : (
          sortedEvents.map((event) => renderEventCard(event))
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 pb-12">
      <SEO title="Calendrier" description="Calendrier des séances de jeu de rôle, one-shots et campagnes de l'association Just Rôle It." path="/calendar" />
      <div className="container mx-auto max-w-6xl">
        <BackButton />
        
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-left md:text-center md:flex-1">
            <h1 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2 md:mb-4 tracking-wide">
              Calendrier des Sessions
            </h1>
            <p className="text-gray-400 text-base md:text-lg">Rejoignez les prochaines aventures !</p>
          </div>
          
          {canCreateEvents() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button">
                  <Plus className="mr-2 h-5 w-5" />
                  Créer un événement
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-purple-300">
                    {editingEvent ? 'Modifier l\'événement' : 'Créer un événement'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-purple-300">Titre</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-300">Date</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="bg-black/50 border-purple-500/30 text-white [&::-webkit-calendar-picker-indicator]:brightness-200 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-300">Heure</Label>
                      <Input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="bg-black/50 border-purple-500/30 text-white [&::-webkit-calendar-picker-indicator]:brightness-200 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-300">Jeu</Label>
                    {games.length > 0 ? (
                      <Select
                        value={formData.game_type}
                        onValueChange={(value) => setFormData({ ...formData, game_type: value })}
                      >
                        <SelectTrigger className="bg-black/50 border-purple-500/30 text-white">
                          <SelectValue placeholder="Sélectionner un jeu..." />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-purple-500/50">
                          {games.map((game) => (
                            <SelectItem 
                              key={game.game_id} 
                              value={game.name}
                              className="text-white hover:bg-purple-900/50"
                            >
                              {game.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-gray-400 text-sm italic">Aucun jeu dans la bibliothèque.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-300">Description <span className="text-gray-500 font-normal">(optionnelle)</span></Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white min-h-[100px]"
                      data-testid="event-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-300">Nombre maximum de joueurs</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={formData.max_players}
                      onChange={(e) => setFormData({ ...formData, max_players: parseInt(e.target.value) })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-300 flex items-center">
                        <MapPin className="h-4 w-4 mr-2" />
                        Lieu (partie physique)
                      </Label>
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="bg-black/50 border-purple-500/30 text-white"
                        placeholder="Ex: 123 Rue du JDR, Paris"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-300 flex items-center">
                        <Video className="h-4 w-4 mr-2" />
                        Lien (partie en ligne)
                      </Label>
                      <Input
                        value={formData.online_link}
                        onChange={(e) => setFormData({ ...formData, online_link: e.target.value })}
                        className="bg-black/50 border-purple-500/30 text-white"
                        placeholder="Ex: https://discord.gg/..."
                      />
                    </div>
                  </div>
                  
                  {/* Session Type and Visibility */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-300">Type de session</Label>
                      <Select
                        value={formData.session_type}
                        onValueChange={(value) => setFormData({ ...formData, session_type: value })}
                      >
                        <SelectTrigger className="bg-black/50 border-purple-500/30 text-white">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-purple-500/50">
                          <SelectItem value="oneshot" className="text-white hover:bg-purple-900/50">
                            One-Shot (une seule session)
                          </SelectItem>
                          <SelectItem value="mini_campaign" className="text-white hover:bg-purple-900/50">
                            Mini Campagne (2-5 sessions)
                          </SelectItem>
                          <SelectItem value="campaign" className="text-white hover:bg-purple-900/50">
                            Campagne (longue durée)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-300">Visibilité</Label>
                      <Select
                        value={formData.is_public ? "public" : "private"}
                        onValueChange={(value) => setFormData({ ...formData, is_public: value === "public" })}
                      >
                        <SelectTrigger className="bg-black/50 border-purple-500/30 text-white">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-purple-500/50">
                          <SelectItem value="public" className="text-white hover:bg-purple-900/50">
                            Public (ouvert aux invités)
                          </SelectItem>
                          <SelectItem value="private" className="text-white hover:bg-purple-900/50">
                            Privé (membres uniquement)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400">
                        {formData.is_public 
                          ? "Les invités connectés peuvent s'inscrire" 
                          : "Seuls les membres peuvent s'inscrire"}
                      </p>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="space-y-2">
                    <Label className="text-purple-300">Table</Label>
                    <Select
                      value={formData.table_id || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, table_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className="bg-black/50 border-purple-500/30 text-white" data-testid="event-table-select">
                        <SelectValue placeholder="Aucune table" />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-purple-500/50">
                        <SelectItem value="none" className="text-gray-400 hover:bg-purple-900/50">Aucune table</SelectItem>
                        {tables.map((t) => (
                          <SelectItem key={t.table_id} value={t.table_id} data-testid={`event-table-option-${t.table_id}`} className="text-white hover:bg-purple-900/50">
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400">Le nom de la table sera ajouté après le titre de l'événement.</p>
                  </div>
                  
                  <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button">
                    {editingEvent ? 'Modifier' : 'Créer'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* View Mode Toggle & Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          {/* View Mode Buttons */}
          <div className="flex gap-2 bg-black/30 p-1 rounded-lg border border-purple-500/30">
            <Button
              variant={viewMode === 'agenda' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('agenda')}
              className={viewMode === 'agenda' ? 'bg-purple-600' : 'text-purple-400 hover:text-purple-300'}
              data-testid="view-agenda-btn"
            >
              <List className="h-4 w-4 mr-2" />
              Agenda
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('week')}
              className={viewMode === 'week' ? 'bg-purple-600' : 'text-purple-400 hover:text-purple-300'}
              data-testid="view-week-btn"
            >
              <CalendarDays className="h-4 w-4 mr-2" />
              Semaine
            </Button>
            <Button
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('month')}
              className={viewMode === 'month' ? 'bg-purple-600' : 'text-purple-400 hover:text-purple-300'}
              data-testid="view-month-btn"
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              Mois
            </Button>
          </div>

          {/* Game Filter */}
          <div className="flex items-center gap-2">
            <Dices className="h-5 w-5 text-purple-400" />
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="w-[180px] bg-black/50 border-purple-500/30 text-white">
                <SelectValue placeholder="Filtrer par jeu" />
              </SelectTrigger>
              <SelectContent className="bg-black border-purple-500/50">
                <SelectItem value="all" className="text-white hover:bg-purple-900/50">
                  Tous les jeux
                </SelectItem>
                {games.map((game) => (
                  <SelectItem 
                    key={game.game_id} 
                    value={game.name}
                    className="text-white hover:bg-purple-900/50"
                  >
                    {game.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {gameFilter !== 'all' && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setGameFilter('all')}
                className="text-purple-400 hover:text-purple-300"
              >
                ✕
              </Button>
            )}
          </div>
        </div>

        {/* Calendar Content */}
        {viewMode === 'agenda' && renderAgendaView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}

        {/* Event Detail Modal */}
        <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
          <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedEvent && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-purple-300 flex items-center gap-3">
                    <Dices className="h-6 w-6" />
                    {fullTitle(selectedEvent)}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-purple-600/80 text-white">
                      {selectedEvent.game_type}
                    </Badge>
                    {selectedEvent.session_type && (
                      <Badge className="bg-purple-600/60 text-purple-200 border-purple-400/50">
                        {getSessionTypeLabel(selectedEvent.session_type)}
                      </Badge>
                    )}
                    <Badge className={selectedEvent.is_public !== false 
                      ? "bg-green-600/60 text-green-200 border-green-400/50" 
                      : "bg-orange-600/60 text-orange-200 border-orange-400/50"
                    }>
                      {selectedEvent.is_public !== false ? 'Public' : 'Membres'}
                    </Badge>
                  </div>
                  
                  {/* Date and Time */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center space-x-2 text-gray-300">
                      <CalendarIcon className="h-4 w-4 text-purple-400" />
                      <span>{new Date(selectedEvent.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Clock className="h-4 w-4 text-purple-400" />
                      <span>{selectedEvent.time}</span>
                    </div>
                  </div>
                  
                  {/* Location - Only visible to logged-in users */}
                  {user && (selectedEvent.location || selectedEvent.online_link) && (
                    <div className="flex flex-wrap gap-4 text-sm">
                      {selectedEvent.location && (
                        <div className="flex items-center space-x-2 text-gray-300">
                          <MapPin className="h-4 w-4 text-green-400" />
                          <span>{selectedEvent.location}</span>
                        </div>
                      )}
                      {selectedEvent.online_link && (
                        <a 
                          href={selectedEvent.online_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-blue-400 hover:text-blue-300"
                        >
                          <Video className="h-4 w-4" />
                          <span>Rejoindre en ligne</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Description */}
                  {selectedEvent.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-purple-300 mb-1">Description</h4>
                      <p className="text-gray-300">{selectedEvent.description}</p>
                    </div>
                  )}
                  
                  {/* Players */}
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-purple-400" />
                    <span className="text-gray-300">
                      {selectedEvent.current_players} / {selectedEvent.max_players} joueurs
                    </span>
                    {selectedEvent.user_is_participating && (
                      <Badge className="bg-green-600/80 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Inscrit
                      </Badge>
                    )}
                  </div>
                  
                  {/* Participants - Only visible to logged-in users */}
                  {user && selectedEvent.participants?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Participants :</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedEvent.participants.map((participant, index) => (
                          <Badge key={index} className="bg-purple-900/50 text-purple-300 border-purple-500/40">
                            {participant.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-purple-500/30">
                    {/* Join/Leave Button - Different states based on user role and event visibility */}
                    {!user ? (
                      /* Visitor (not logged in) - Show login prompt */
                      <Button
                        onClick={() => window.location.href = '/login'}
                        variant="outline"
                        className="border-purple-500/50 text-purple-400"
                      >
                        Connectez-vous pour rejoindre
                      </Button>
                    ) : selectedEvent.user_is_participating ? (
                      /* User is already in event - Show leave button */
                      <Button
                        onClick={() => { handleLeaveEvent(selectedEvent.event_id); setSelectedEvent(null); }}
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-900/30"
                      >
                        Se désinscrire
                      </Button>
                    ) : !canJoinEvent(selectedEvent) ? (
                      /* Guest trying to join private event */
                      <Button
                        disabled
                        className="bg-orange-600/30 text-orange-300 cursor-not-allowed"
                      >
                        Réservé aux membres
                      </Button>
                    ) : (
                      /* Can join */
                      <Button
                        onClick={() => { handleJoinEvent(selectedEvent.event_id); setSelectedEvent(null); }}
                        disabled={selectedEvent.current_players >= selectedEvent.max_players}
                        className="bg-purple-600 hover:bg-purple-500"
                      >
                        {selectedEvent.current_players >= selectedEvent.max_players ? 'Complet' : 'Rejoindre'}
                      </Button>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="border-purple-500/50 text-purple-400">
                          <Download className="mr-2 h-4 w-4" />
                          Exporter
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-black/95 border-purple-500/50">
                        <DropdownMenuItem 
                          onClick={() => window.open(getGoogleCalendarUrl(selectedEvent), '_blank')}
                          className="text-purple-300 hover:bg-purple-900/50 cursor-pointer"
                        >
                          Google Calendar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => downloadICS(selectedEvent)}
                          className="text-purple-300 hover:bg-purple-900/50 cursor-pointer"
                        >
                          Fichier ICS
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {(isAdmin() || (canCreateEvents() && selectedEvent.created_by === user?.user_id)) && (
                      <>
                        <Button
                          onClick={() => openEditDialog(selectedEvent)}
                          variant="outline"
                          className="border-purple-500/50 text-purple-400"
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Modifier
                        </Button>
                        <Button
                          onClick={() => handleDeleteEvent(selectedEvent.event_id)}
                          variant="outline"
                          className="border-red-500/50 text-red-400"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Supprimer
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Calendar;
